import { createApi } from '@reduxjs/toolkit/query/react'
import type { EntityVersion, Invite, ShareItem, Slug } from '@tunasong/models'
import { dedupe, isStoredObject, logger, removeEntityReserved } from '@tunasong/models'
import type {
  AccessRequestState,
  AccessRequestView,
  ACL,
  ApprovablePermission,
  Edge,
  EdgeType,
  Entity,
  EntityMediaResponse,
  EntityType,
  Persisted,
} from '@tunasong/schemas'
import type { EntitySync } from '@tunasong/sync-lib'
import { entitiesSlice } from '../features/entities/entities-slice.js'
import baseQuery, { createCachedQuerySpecHelper } from './base-query.js'
import { createEntityBody } from './create-entity-body.js'
import { clearYDocCache } from './ydoc-cache.js'

/** StartKey for API. This is opaque for clients (but is a url encoded JSON string) */
export type StartKey = string

/** The default fields to load when loading   all entities */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const defaultFields = [
  'id',
  'parentId',
  'userId',
  'type',
  'acls',
  'name',
  'tags',
  'starred',
  'media',
  'description',
  /** Folder */
  'options',
  /** Audio */
  'url',
  'storage',
  'features',
  'overdub',
  'regions',
  /** Playlist */
  'mediaIds',
  /** Common */
  'trash',
  'metadata',
  'createdAt',
  'updatedAt',
]

/** id for partial list for pagination @see https://redux-toolkit.js.org/rtk-query/usage/pagination#automated-re-fetching-of-paginated-queries */
export const PARTIAL_LIST = 'PARTIAL_LIST'

// const loadAllProps = { partial: true, trash: false, fields: defaultFields }

// Define a service using a base URL and expected endpoints

// Create a local query helper
const apiName = 'entities-api'
const cachedQuerySpec = createCachedQuerySpecHelper(apiName)

const tagTypes = [
  'Entity',
  'EntityEdges',
  'EntityBacklinks',
  'ChildEntities',
  'RootEntities',
  'AccessRequests',
] as const
type TagType = (typeof tagTypes)[number]
export const entitiesApi = createApi({
  reducerPath: apiName,
  tagTypes,
  baseQuery,
  keepUnusedDataFor: 5,

  endpoints: builder => ({
    loadEntity: builder.query<Persisted<Entity>, { id: string }>({
      providesTags: (result, error, id) => [{ type: 'Entity', id: id.id }],

      query: cachedQuerySpec('loadEntity', ({ id }) => ({
        url: `entities/${id}`,
      })),
      onQueryStarted: async (ids, { dispatch, queryFulfilled }) => {
        void queryFulfilled
          .then(({ meta }) => {
            const eTag = meta?.eTag
            if (eTag) {
              dispatch(entitiesSlice.actions.setEtag({ id: ids.id, eTag }))
            }
          })
          .catch(e => {
            logger.warn('Error loading entity', e)
          })
      },
    }),
    timeTravelChanges: builder.query<EntitySync[], { id: string }>({
      query: ({ id }) => `entities/${id}/timetravel`,
    }),
    timeTravel: builder.query<Persisted<Entity>, { entityId: string; createdAtId: string }>({
      // keepUnusedDataFor: 0,
      query: ({ entityId, createdAtId }) => `entities/${entityId}/timetravel?at=${createdAtId}`,
    }),

    loadEntityEdges: builder.query<Edge[], { source: string; target?: string }>({
      providesTags: (result, error, { source }) => [{ id: source, type: 'EntityEdges' }],
      query: cachedQuerySpec('loadEntityEdges', ({ source, target }) => ({
        url: `entities/${source}/edges${target ? `?target=${target}` : ''}`,
      })),
    }),

    loadEntityEdgesByIds: builder.query<Edge[], { sources: string[]; target?: string; relation?: EdgeType }>({
      // providesTags: (result, error, { source, target, relation }) => [{ id: source, type: 'EntityEdges' }],

      query: cachedQuerySpec('loadEntityEdgesByIds', ({ sources, target, relation }) => ({
        url: `entities/edges?sourceIds=${sources.join(',')}${target ? `&target=${target}` : ''}${relation ? `&relation=${relation}` : ''}`,
      })),
    }),

    /** Load the entities on the target side of an edge */
    loadEdgeEntitiesByIds: builder.query<
      Persisted<Entity>[],
      { sourceIds: string[]; target?: string; relation?: EdgeType }
    >({
      // providesTags: (result, error, { source, target, relation }) => [{ id: source, type: 'EntityEdges' }],
      query: cachedQuerySpec('loadEdgeEntitiesByIds', ({ sourceIds, target, relation }) => ({
        url: `entities/edge-entities?sourceIds=${sourceIds.join(',')}${target ? `&target=${target}` : ''}${relation ? `&relation=${relation}` : ''}`,
      })),
    }),

    createEntityEdge: builder.mutation<Edge, { edge: Edge }>({
      // we maintain these manually in onQueryStarted
      invalidatesTags: edge => {
        if (!edge) {
          return []
        }
        // logger.debug('Create edge invalidates EntityEdges tag', edge.source)
        return [
          { type: 'EntityEdges', id: edge.source },
          { type: 'EntityBacklinks', id: edge.target },
        ]
      },

      query: ({ edge }) => ({
        method: 'POST',
        url: `entities/${edge.source}/edges`,
        body: edge,
      }),
      onQueryStarted: async ({ edge }, api) => {
        const { dispatch } = api

        /** If the entity has an id we push it to the child list immediately */
        const { source, target } = edge

        // If we have not loaded children before we need to upsert
        const existingEdges =
          entitiesApi.endpoints.loadEntityEdges.select({ source, target })(api.getState()).data ?? []
        const existingBacklinks =
          entitiesApi.endpoints.loadEntityBacklinks.select({ id: target })(api.getState()).data ?? []
        void dispatch(entitiesApi.util.upsertQueryData('loadEntityEdges', { source, target }, [...existingEdges, edge]))
        void dispatch(
          entitiesApi.util.upsertQueryData('loadEntityBacklinks', { id: target }, [...existingBacklinks, edge])
        )
      },
    }),
    deleteEntityEdge: builder.mutation<void, Edge>({
      invalidatesTags: (result, error, { source, target }) => {
        logger.debug(`Delete edge invalidates EntityEdges ${source} tag and EntityBacklinks ${target}`)
        return [
          { type: 'EntityEdges', id: source },
          { type: 'EntityBacklinks', id: target },
        ]
      },

      query: edge => ({
        method: 'DELETE',
        url: `entities/${edge.source}/edges`,
        body: edge,
      }),
    }),
    loadEntityBacklinks: builder.query<Edge[], { id: string }>({
      providesTags: (result, error, id) => [{ type: 'EntityBacklinks', id: id.id }],
      query: cachedQuerySpec('loadEntityBacklinks', ({ id }) => ({
        url: `entities/${id}/backlinks`,
      })),
    }),
    loadEntities: builder.query<Persisted<Entity>[], { ids: string[] }>({
      providesTags: result => {
        const tags = result?.map(i => ({ type: 'Entity' as const, id: i.id })) ?? []
        return tags
      },
      query: cachedQuerySpec('loadEntities', ({ ids }) => ({
        url: `entities/?ids=${encodeURIComponent(dedupe(ids).join(','))}`,
      })),
    }),
    /**
     * Media an entity references, readable through it (shared-container media access).
     * Deliberately NOT tagged `Entity`: these are projections and must never enter the entity
     * cache. `refsKey` is never sent — it only makes a changed reference list a new cache key.
     */
    loadEntityMedia: builder.query<EntityMediaResponse, { id: string; refsKey: string }>({
      query: ({ id }) => `entities/${encodeURIComponent(id)}/media`,
    }),
    /** @deprecated */
    loadEntityPublic: builder.query<{ entity: Persisted<Entity>; authorizedUrl: string | null }, { id: string }>({
      providesTags: (result, error, { id }) => [{ type: 'Entity' as const, id }],
      query: ({ id }) => `entities/${id}/public`,
      transformResponse: (entity: Persisted<Entity>, meta) => {
        const authorizedUrl = isStoredObject(entity) ? (meta?.response?.headers.get('authorized-url') ?? null) : null
        return { entity, authorizedUrl }
      },
    }),
    loadEntityHistory: builder.query<EntityVersion[], { id: string }>({
      query: ({ id }) => `entities/${id}/history`,
    }),
    restoreEntityVersion: builder.mutation<Persisted<Entity>, { version: EntityVersion }>({
      invalidatesTags: (result, error, { version }) => [{ type: 'Entity', id: version.id }],
      query: ({ version }) => `entities/${version.id}/restore?version=${version.version}`,
      onQueryStarted: async ({ version }, { dispatch, queryFulfilled }) => {
        /** Clear the YDoc cache for the entity */
        clearYDocCache(version.id)
        void queryFulfilled
          .then(({ data: entity }) => {
            void dispatch(entitiesApi.util.upsertQueryData('loadEntity', { id: entity.id }, entity))
          })
          .catch(e => {
            logger.warn('Restore entity failed', e)
          })
      },
    }),
    /** Restore to a previous YDoc. Just an update - a bit of a hack...p */
    entityTimeTravelRestore: builder.mutation<Persisted<Entity>, { id: string; timeTravelTo: string }>({
      invalidatesTags: (result, error, { id }) => [{ type: 'Entity', id }],

      query: ({ timeTravelTo, id }) => ({
        url: `entities/${id}/restore?timeTravelTo=${timeTravelTo}`,
      }),
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
        /** Clear the YDoc cache for the entity */
        clearYDocCache(id)
        void queryFulfilled
          .then(({ data }) => {
            void dispatch(entitiesApi.util.upsertQueryData('loadEntity', { id: data.id }, data))
          })
          .catch(e => {
            logger.warn('Time travel restore failed', e)
          })
      },
    }),
    loadAllEntities: builder.query<
      Persisted<Entity>[],
      {
        partial?: boolean
        trash?: boolean
        fields?: string[]
      }
    >({
      providesTags: ['RootEntities', { type: 'Entity' }],

      query: cachedQuerySpec('loadAllEntities', ({ partial, trash, fields = [] }) => ({
        url: `entities/?fields=${fields.join(',')}${partial ? '&partial=true' : ''}${trash ? '&trash=true' : ''}`,
      })),
    }),
    /** Partial variant of loadAll, typically used for trash */
    loadAllEntitiesPartial: builder.query<
      { entities: Persisted<Entity>[]; resumeKey: StartKey | null },
      {
        partial?: boolean
        trash?: boolean
        fields?: string[]
        limit?: number
        startKey?: string | null
      }
    >({
      providesTags: ['RootEntities', { type: 'Entity', id: PARTIAL_LIST }],
      query: cachedQuerySpec('loadAllEntitiesPartial', ({ partial, trash, fields = [], startKey, limit = 100 }) => ({
        url: `entities/?fields=${fields.join(',')}${partial ? '&partial=true' : ''}${trash ? '&trash=true' : ''}${
          limit ? '&limit=' + limit : ''
        }${startKey ? '&startKey=' + startKey : ''}`,
      })),

      transformResponse: (baseQueryReturnValue, meta) => {
        const resumeKey = meta?.resumeKey ?? null
        return { entities: baseQueryReturnValue as Persisted<Entity>[], resumeKey }
      },
    }),

    /** Child entities are used for e.g., chat comments. When loading incrementally, we need to invalidate */
    loadChildEntitiesPartial: builder.query<
      { entities: Persisted<Entity>[]; resumeKey: StartKey | null },
      {
        parentId: string
        type?: EntityType
        fields?: string
        limit?: number
        startKey?: string | null
      }
    >({
      providesTags: (result, error, { parentId }) => {
        const entityTags = result?.entities?.map(r => ({ type: 'Entity' as const, id: r.id })) ?? []
        return [{ type: 'ChildEntities', id: parentId }, { type: 'ChildEntities', id: PARTIAL_LIST }, ...entityTags]
      },
      transformResponse: (baseQueryReturnValue, meta) => {
        const resumeKey = meta?.resumeKey ?? null
        return { entities: baseQueryReturnValue as Persisted<Entity>[], resumeKey }
      },
      query: cachedQuerySpec('loadChildEntitiesPartial', ({ parentId, type, fields, startKey, limit }) => ({
        url: `entities/?parentId=${parentId}${fields ? '&fields=' + fields : ''}${type ? '&type=' + type : ''}${
          limit ? '&limit=' + limit : ''
        }${startKey ? '&startKey=' + startKey : ''}`,
      })),
    }),
    loadChildEntities: builder.query<
      Persisted<Entity>[],
      {
        parentId: string
        type?: EntityType
        fields?: string
      }
    >({
      providesTags: (result, error, { parentId }) => {
        const entityTags = result?.map(r => ({ type: 'Entity' as const, id: r.id })) ?? []
        return [{ type: 'ChildEntities', id: parentId }, ...entityTags]
      },

      query: cachedQuerySpec('loadChildEntities', ({ parentId, type, fields }) => ({
        url: `entities/?parentId=${parentId}${fields ? '&fields=' + fields : ''}${type ? '&type=' + type : ''}`,
      })),
    }),
    loadChildEntitiesBatch: builder.query<
      Persisted<Entity>[],
      {
        parentIds: string[]
        type?: EntityType
        fields?: string
      }
    >({
      providesTags: (result, _error, { parentIds }) => {
        const entityTags = result?.map(r => ({ type: 'Entity' as const, id: r.id })) ?? []
        const parentTags = parentIds.map(id => ({ type: 'ChildEntities' as const, id }))
        return [...parentTags, ...entityTags]
      },
      query: cachedQuerySpec('loadChildEntitiesBatch', ({ parentIds, type, fields }) => ({
        url: `entities/?parentIds=${parentIds.join(',')}${fields ? '&fields=' + fields : ''}${type ? '&type=' + type : ''}`,
      })),
    }),
    createEntity: builder.mutation<
      Persisted<Entity>,
      { entity: Entity; parent: Persisted<Entity> | null; isPrivate?: boolean }
    >({
      invalidatesTags: result => {
        /** For root entities we need to invalidate the root */
        if (!result?.parentId) {
          return [{ type: 'RootEntities' as const }]
        }
        logger.debug('Create entity invalidates ChildEntities tag', result.parentId)
        return [{ type: 'ChildEntities', id: result?.parentId }]
      },
      query: ({ entity, parent, isPrivate = false }) => ({
        url: `entities/`,
        method: 'POST',
        body: createEntityBody({ entity, parent, isPrivate }),
      }),
    }),
    cloneEntity: builder.mutation<Persisted<Entity>, { entityId: string; updatedProps?: Partial<Persisted<Entity>> }>({
      invalidatesTags: result => {
        /** For root entities we need to invalidate the root */
        if (!result?.parentId) {
          return [{ type: 'RootEntities' as const }]
        }
        return [{ type: 'ChildEntities', id: result?.parentId }]
      },
      query: ({ entityId, updatedProps }) => ({
        url: `entities/${entityId}/clone`,
        method: 'POST',
        body: updatedProps,
      }),
    }),
    updateEntity: builder.mutation<
      Persisted<Entity>,
      { id: string; partialEntity: Partial<Entity>; eTag: string | null; upsert?: boolean }
    >({
      /** Reload all entities (e.g., child entities) after updating an entity */
      invalidatesTags: (result, error, { id, partialEntity }) => {
        const invalidation: { type: TagType; id: string | undefined }[] = [
          { type: 'Entity', id },
          { type: 'ChildEntities', id: result?.parentId },
        ]
        /**
         * Change the parentId or without parent => need to reload root
         * @note MUST filter out null values otherwise the invalidation breaks
         */
        if (!result?.parentId || partialEntity.parentId) {
          invalidation.push({ type: 'RootEntities', id: undefined })
        }
        logger.debug('Update entity invalidates ChildEntities tag', invalidation)
        return invalidation
      },
      query: ({ id, partialEntity, eTag, upsert = false }) => {
        const headers: Record<string, string> = {}
        /**
         * We need a strategy to update entities that have changed server-side.
         * Document updates are handled using Y - so no worries there.
         * However, we must include yDoc in the eTag, otherwise clients will not get the last content.
         * Single attribute updates are accepted below.
         * Multi-attribute updates will require eTag to be the same.
         */
        const numKeysUpdated = Object.keys(partialEntity).length
        if (eTag && numKeysUpdated > 1) {
          headers['if-match'] = eTag
        }
        const body = removeEntityReserved(partialEntity)
        return {
          url: `entities/${id}`,
          method: upsert ? 'PUT' : 'PATCH',
          headers,
          body,
        }
      },
      /**
       * Optimistic update for the RTK Query `loadEntity` cache.
       *
       * Pairs with the graph-cache optimistic update in
       * `lib/graph-lib/src/react/graph.hook.ts` (createCachedGraph's
       * `useEntityUpdate` calls `optimisticUpdateCache` synchronously, before
       * the API call). The two caches are reconciled in `useEntity` via
       * `pickLatest` — whichever entry has the newer server-issued
       * `updatedAt` wins. The graph-cache patch keeps the `updatedAt` of the
       * version it edited and marks the entry pending, so it wins a tie with
       * the RTK entry but never outranks a newer server version (GH #1913).
       *
       * This RTK-level patch is additive: it ensures the `loadEntity` cache
       * tracks the new value too, so consumers that read RTK Query directly
       * (or paths where the graph cache hasn't been populated yet —
       * `optimisticUpdateCache` returns `undefined` without writing if
       * the entry doesn't exist in the Zustand store) still see the
       * optimistic value. Without this,
       * the RTK cache stays at the last-fetched value until
       * `invalidatesTags` triggers a refetch ~200-500ms after the mutation.
       *
       * Race vs. WS push during the debounce window: a collaborator's
       * `receiveEntity` push can arrive while a local edit is debouncing.
       * A newer pushed version supersedes the pending graph-cache entry
       * (last-writer-wins on the server `updatedAt`); this RTK patch likewise gets
       * overwritten when `invalidatesTags` refetches the server-authoritative
       * state. Net effect: last-writer-wins for discrete-choice fields
       * (scale, meter, sectionType, tempo) — the expected UX. Brief
       * cross-user flicker on simultaneous edits is acceptable.
       *
       * On mutation failure (eTag conflict, network error, validation),
       * `patch.undo()` reverts this layer's local cache. The graph cache's
       * pending entry is not unwound by `patch.undo()`: a refetch of the
       * same server version does not replace it, so it stays until a newer
       * server version arrives (as it did before GH #1913).
       */
      onQueryStarted: async ({ id, partialEntity }, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          entitiesApi.util.updateQueryData('loadEntity', { id }, draft => {
            // RTK passes the raw cached value when it is not draftable, so a
            // cache entry that resolved to `null` arrives here as `null` and
            // `Object.assign` throws. Skip the optimistic patch — the
            // `invalidatesTags` refetch brings the cache to server state. GH #1730
            if (!draft) {
              return
            }
            Object.assign(draft, partialEntity)
          })
        )
        try {
          await queryFulfilled
        } catch (err) {
          patch.undo()
          logger.warn('Update entity failed, rolling back optimistic patch', { id, err })
        }
      },
    }),
    deleteEntity: builder.mutation<void, { entity: Persisted<Entity> }>({
      /** Reload all entities (e.g., child entities) after updating an entity */
      invalidatesTags: (result, error, { entity }) => [
        { type: 'RootEntities' },
        { type: 'ChildEntities', id: entity.parentId ?? undefined },
        { type: 'ChildEntities', id: PARTIAL_LIST },
        { type: 'Entity', id: entity.id },
        { type: 'Entity', id: PARTIAL_LIST },
      ],
      query: ({ entity }) => ({
        url: `entities/${entity.id}`,
        method: 'DELETE',
      }),
    }),
    transferEntity: builder.mutation<void, { entity: Persisted<Entity>; toUserId: string }>({
      invalidatesTags: (result, error, { entity }) => [
        { type: 'RootEntities' },
        { type: 'ChildEntities', id: entity.parentId ?? undefined },
        { type: 'ChildEntities', id: PARTIAL_LIST },
        { type: 'Entity', id: entity.id },
        { type: 'Entity', id: PARTIAL_LIST },
      ],
      query: ({ entity, toUserId }) => ({
        url: `entities/${entity.id}/transfer`,
        method: 'POST',
        body: { toUserId },
      }),
    }),
    /**
     * The owner confirms media flagged at an ownership transfer, so the container lends it to its readers again
     * (GH #2233). Reloads the container, whose `unconfirmedMediaIds` the server owns.
     */
    confirmEntityMedia: builder.mutation<{ unconfirmedMediaIds: string[] }, { entityId: string; ids: string[] }>({
      invalidatesTags: (result, error, { entityId }) => [{ type: 'Entity', id: entityId }],
      query: ({ entityId, ids }) => ({
        url: `entities/${encodeURIComponent(entityId)}/media/confirm`,
        method: 'POST',
        body: { ids },
      }),
    }),
    /** Social API */
    share: builder.mutation<Persisted<ShareItem>, { item: Persisted<ShareItem>; acls: ACL[] }>({
      /**
       * Whenever we share an item, we need to reload entities from the server,
       * because child entities may have been updated.
       */
      invalidatesTags: (result, error, { item }) => [
        { type: 'Entity' as const, id: item.id },
        { type: 'ChildEntities' as const, id: item.id },
        /** "Add person" satisfies a pending access request (request-access spec §6.7) */
        { type: 'AccessRequests' as const, id: item.id },
        { type: 'AccessRequests' as const, id: 'owner' },
      ],

      query: ({ item, acls }) => ({
        url: `social/shares`,
        method: 'POST',
        body: { id: item.id, acls },
      }),
      onQueryStarted: ({ item, acls }, { dispatch }) => {
        /** Update ACLs in local item */
        dispatch(
          entitiesApi.util.updateQueryData('loadEntity', { id: item.id }, draft => {
            // See the `updateEntity` recipe above — a null cache entry reaches
            // the recipe verbatim. This `onQueryStarted` is synchronous, so an
            // unguarded throw rejects the share mutation itself. GH #1730
            if (!draft) {
              return
            }
            Object.assign(draft, { acls })
          })
        )
      },
    }),

    invite: builder.mutation<Slug, { invite: Invite }>({
      query: ({ invite }) => ({
        url: `social/invite`,
        method: 'POST',
        body: invite,
      }),
    }),

    /**
     * Request access (docs/superpowers/specs/2026-10-17-request-access-design.md §7.4). Not cached by the service
     * worker: a stale "Request sent" or a stale approver list would be wrong, not just old.
     */
    requestAccess: builder.mutation<AccessRequestState, { id: string }>({
      query: ({ id }) => ({ url: `entities/${id}/access-requests`, method: 'POST' }),
      invalidatesTags: (result, error, { id }) => [{ type: 'AccessRequests' as const, id: `mine-${id}` }],
    }),
    myAccessRequest: builder.query<AccessRequestState, { id: string }>({
      query: ({ id }) => `entities/${id}/access-requests/mine`,
      providesTags: (result, error, { id }) => [{ type: 'AccessRequests' as const, id: `mine-${id}` }],
    }),
    entityAccessRequests: builder.query<AccessRequestView[], { id: string }>({
      query: ({ id }) => `entities/${id}/access-requests`,
      providesTags: (result, error, { id }) => [{ type: 'AccessRequests' as const, id }],
    }),
    ownerAccessRequests: builder.query<AccessRequestView[], void>({
      query: () => `entities/access-requests`,
      providesTags: [{ type: 'AccessRequests' as const, id: 'owner' }],
    }),
    approveAccessRequest: builder.mutation<
      ACL[],
      { entityId: string; requesterId: string; permission: ApprovablePermission }
    >({
      query: ({ entityId, requesterId, permission }) => ({
        url: `social/shares/requests/${entityId}/${requesterId}`,
        method: 'POST',
        body: { permission },
      }),
      /** Invalidated on error too: a 409/404 means the lists are stale */
      invalidatesTags: (result, error, { entityId }) => [
        { type: 'AccessRequests' as const, id: entityId },
        { type: 'AccessRequests' as const, id: 'owner' },
        { type: 'Entity' as const, id: entityId },
        { type: 'ChildEntities' as const, id: entityId },
      ],
    }),
    declineAccessRequest: builder.mutation<void, { entityId: string; requesterId: string }>({
      query: ({ entityId, requesterId }) => ({
        url: `entities/${entityId}/access-requests/${requesterId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { entityId }) => [
        { type: 'AccessRequests' as const, id: entityId },
        { type: 'AccessRequests' as const, id: 'owner' },
      ],
    }),
    /** Undo of a decline (spec §6.6) */
    reopenAccessRequest: builder.mutation<void, { entityId: string; requesterId: string }>({
      query: ({ entityId, requesterId }) => ({
        url: `entities/${entityId}/access-requests/${requesterId}/reopen`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { entityId }) => [
        { type: 'AccessRequests' as const, id: entityId },
        { type: 'AccessRequests' as const, id: 'owner' },
      ],
    }),
  }),
})
